import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import clsx from "clsx";
import * as auth from "../../store/ducks/auth.duck";
import { verifyMfaLogin, getMfaMethods } from "../../crud/auth.crud";

function MfaVerify({ mfaData, onBack, onSuccess }) {
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [code, setCode] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);
  const singleInputRef = useRef(null);

  useEffect(() => {
    loadMethods();
  }, [mfaData.sid]);

  useEffect(() => {
    if (!selectedMethod) return;

    const { inputConfig } = selectedMethod;
    setCode(Array(inputConfig.length).fill(""));
    setError("");

    setTimeout(() => {
      if (inputConfig.type === "alphanumeric") {
        if (singleInputRef.current) singleInputRef.current.focus();
      } else if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 100);
  }, [selectedMethod]);

  const loadMethods = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await getMfaMethods(mfaData.sid);
      const { methods: availableMethods, defaultMethod } = res.data;
      setMethods(availableMethods);

      const defaultMethodObj =
        availableMethods.find((m) => m.type === defaultMethod) ||
        availableMethods[0];
      setSelectedMethod(defaultMethodObj);
    } catch (err) {
      if (err.response?.status === 410) {
        setError("Sessão expirada. Faça login novamente.");
      } else {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Erro ao carregar métodos de verificação."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNumericChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError("");

    if (value && index < code.length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (
      e.key === "Backspace" &&
      !code[index] &&
      index > 0 &&
      inputRefs.current[index - 1]
    ) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const { inputConfig } = selectedMethod;
    const pastedData = e.clipboardData
      .getData("text")
      .replace(inputConfig.type === "numeric" ? /\D/g : /[^a-zA-Z0-9]/g, "")
      .slice(0, inputConfig.length);

    if (inputConfig.type === "alphanumeric") {
      setCode(pastedData.toUpperCase().split(""));
    } else {
      const newCode = [...code];
      for (let i = 0; i < pastedData.length; i++) {
        newCode[i] = pastedData[i];
      }
      setCode(newCode);
      const nextIndex = Math.min(pastedData.length, code.length - 1);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
    }
  };

  const handleAlphanumericChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const { inputConfig } = selectedMethod;
    setCode(value.slice(0, inputConfig.length).split(""));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullCode = code.join("");
    const { inputConfig } = selectedMethod;

    if (fullCode.length !== inputConfig.length) {
      setError(
        inputConfig.type === "numeric"
          ? `Digite o código de ${inputConfig.length} dígitos.`
          : `Digite o código de ${inputConfig.length} caracteres.`
      );
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await verifyMfaLogin(mfaData.sid, fullCode);
      await onSuccess(res.data.token);
    } catch (err) {
      if (err.response?.status === 429) {
        setError("Muitas tentativas. Aguarde um minuto e tente novamente.");
      } else if (err.response?.status === 410) {
        setError("Sessão expirada. Faça login novamente.");
      } else {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Código inválido. Tente novamente."
        );
      }
      setCode(Array(inputConfig.length).fill(""));
      if (inputConfig.type === "alphanumeric") {
        if (singleInputRef.current) singleInputRef.current.focus();
      } else if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const switchMethod = (method) => {
    if (method.type === selectedMethod?.type) return;
    setSelectedMethod(method);
  };

  const renderNumericInput = () => {
    const { inputConfig } = selectedMethod;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        {Array.from({ length: inputConfig.length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={code[index] || ""}
            onChange={(e) => handleNumericChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            style={{
              width: "45px",
              height: "55px",
              fontSize: "24px",
              textAlign: "center",
              border: "1px solid #e2e5ec",
              borderRadius: "8px",
              outline: "none",
            }}
            disabled={submitting}
          />
        ))}
      </div>
    );
  };

  const renderAlphanumericInput = () => {
    const { inputConfig } = selectedMethod;

    return (
      <input
        ref={singleInputRef}
        type="text"
        className="form-control"
        placeholder={inputConfig.placeholder || ""}
        value={code.join("")}
        onChange={handleAlphanumericChange}
        onPaste={handlePaste}
        maxLength={inputConfig.length}
        style={{
          textAlign: "center",
          fontSize: "20px",
          letterSpacing: "2px",
          textTransform: "uppercase",
        }}
        disabled={submitting}
      />
    );
  };

  const renderInput = () => {
    if (!selectedMethod) return null;
    const { inputConfig } = selectedMethod;
    return inputConfig.type === "numeric"
      ? renderNumericInput()
      : renderAlphanumericInput();
  };

  const renderMethodSwitch = () => {
    if (methods.length <= 1) return null;

    const otherMethods = methods.filter((m) => m.type !== selectedMethod?.type);

    return (
      <div className="text-center mb-3">
        {otherMethods.map((method) => (
          <button
            key={method.type}
            type="button"
            onClick={() => switchMethod(method)}
            disabled={submitting}
            style={{
              fontSize: "13px",
              background: "none",
              border: "none",
              color: "#5867dd",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {method.type === "backup_code"
              ? "Perdeu acesso? Use um código de backup"
              : "Usar código do aplicativo"}
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="card-login">
        <div className="kt-login__form">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Carregando...</span>
            </div>
            <p className="mt-3 text-muted">
              Carregando opções de verificação...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && methods.length === 0) {
    return (
      <div className="card-login">
        <div className="kt-login__form">
          <div className="alert alert-danger">{error}</div>
          <button
            type="button"
            onClick={onBack}
            className="btn btn-secondary btn-elevate btn-block"
          >
            Voltar ao login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-login">
      <div className="kt-login__form">
        <h3 className="card-login-title">Verificação em duas etapas</h3>
        <h3 className="card-login-subtitle">{selectedMethod?.hint}</h3>

        <form onSubmit={handleSubmit} style={{ marginTop: "32px" }}>
          {error && (
            <div role="alert" className="alert alert-danger">
              <div className="alert-text">{error}</div>
            </div>
          )}

          <div className="form-group">{renderInput()}</div>

          {renderMethodSwitch()}

          <div style={{ marginTop: "24px" }}>
            <button
              type="submit"
              disabled={
                submitting ||
                code.join("").length !== selectedMethod?.inputConfig?.length
              }
              className={`btn btn-primary btn-elevate btn-block ${clsx({
                "kt-spinner kt-spinner--right kt-spinner--md kt-spinner--light": submitting,
              })}`}
            >
              Verificar
            </button>
          </div>

          <div style={{ textAlign: "center", marginTop: "16px" }}>
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              style={{
                fontSize: "14px",
                color: "#6c757d",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "8px 16px",
              }}
            >
              ← Voltar ao login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default connect(null, auth.actions)(MfaVerify);
